import React from 'react';
import useSWR from 'swr';
import { H4, H6, Muted } from './text';
import dynamic from 'next/dynamic';

type FeedItem = {
	title: string;
	link: string | string[];
	pubDate: string;
	contentSnippet?: string;
	description?: string;
};

interface TeamRssFeedProps {
	feedUrl?: string;
	logo?: string;
	colorCode?: string;
}

const fetcher = (url: string) => fetch(url).then((r) => r.json());

const TeamRssFeedClient: React.FC<TeamRssFeedProps> = ({ feedUrl, logo, colorCode }) => {
	const { data } = useSWR<{ items: FeedItem[] }>(
		feedUrl ? `/api/team-rss?url=${encodeURIComponent(feedUrl)}` : null,
		fetcher,
		{ revalidateOnFocus: false, shouldRetryOnError: false },
	);

	const items = data?.items ?? [];

	if (!items || items.length === 0) return null;

	const visibleItems = items.slice(0, 3); // Show max 3 items

	const getArticleLink = (link: string | string[]): string => {
		if (typeof link === 'string') {
			return link;
		}
		if (Array.isArray(link)) {
			// Extract domain from feed URL if available
			let feedDomain = '';
			if (feedUrl) {
				try {
					feedDomain = new URL(feedUrl).hostname;
				} catch (e) {
					// Fallback if URL parsing fails
				}
			}

			// Filter valid article links
			const validLinks = link.filter((l) => {
				if (!l || typeof l !== 'string' || !l.startsWith('http')) return false;

				// Exclude obvious meta/resource links
				if (
					l.includes('rel=') ||
					l.includes('preconnect') ||
					l.includes('dns-prefetch') ||
					l.includes('wpcdnnode.com') ||
					l.includes('cdn.')
				) {
					return false;
				}

				// If we have a feed domain, prefer links from the same domain
				if (feedDomain) {
					try {
						return new URL(l).hostname === feedDomain;
					} catch (e) {
						return false;
					}
				}

				// Fallback: any link with a proper path structure
				return l.split('/').length > 3;
			});

			// Return the first valid link or the longest one
			return (
				validLinks[0] ||
				validLinks.reduce((longest, current) => (current.length > longest.length ? current : longest), '')
			);
		}
		return '';
	};

	const DiagonalStripes: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
		<svg
			viewBox="0 0 40 40"
			version="1.1"
			xmlns="http://www.w3.org/2000/svg"
			xmlnsXlink="http://www.w3.org/1999/xlink"
			{...props}
		>
			<title>diagonal-stripes</title>
			<desc>Created with Sketch.</desc>
			<g id="Page-1" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
				<g id="diagonal-stripes" fill="#000000">
					<polygon id="Path-2" points="0 40 40 0 20 0 0 20" />
					<polygon id="Path-2-Copy" points="40 40 40 20 20 40" />
				</g>
			</g>
		</svg>
	);

	return (
		<div>
			<div className="mb-12">
				<H4 className="mb-6">Clubnieuws</H4>
				<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
					{visibleItems.map((item, idx) => {
						return (
							<a
								className="flex flex-col bg-transparent mb-4 h-auto  rounded-sm"
								key={idx}
								href={getArticleLink(item.link)}
								target="_blank"
								rel="noopener noreferrer"
							>
								<div
									className="flex-shrink-0 aspect-video object-cover rounded-t-sm relative overflow-hidden "
									style={{
										backgroundColor: colorCode || '#e2002b', // Use colorCode if available
									}}
								>
									<img
										src={logo}
										alt={item.title}
										className="absolute top-1/2 left-1/2 w-1/3 h-auto z-10 -translate-y-[50%] -translate-x-[50%]"
									/>
									<div className="absolute left-0 bottom-0 w-full h-full opacity-15 z-0 -translate-y-[100px]">
										<DiagonalStripes className="" />
									</div>
								</div>

								<div className="pl-0 pr-4 pt-2 pb-0">
									<H6 className="mb-2 line-clamp-3">
										<span className="hover:underline">{item.title}</span>
									</H6>
									<Muted className="mb-2">
										{item.pubDate && new Date(item.pubDate).toLocaleDateString()}
									</Muted>
								</div>
							</a>
						);
					})}
				</div>
			</div>
		</div>
	);
};

// Export as dynamic component with SSR disabled
const TeamRssFeed = dynamic(() => Promise.resolve(TeamRssFeedClient), {
	ssr: false,
	loading: () => <div>Team feed laden...</div>,
});

export default TeamRssFeed;
